import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import * as XLSX from 'xlsx';
import { format, differenceInYears } from 'date-fns';

const MAP_PREOP_TEMPO = {
  ate_30_dias: 'Até 30 dias', ate_6_meses: 'Até 6 meses', ate_1_ano: 'Até 1 ano',
  ate_5_anos: 'Até 5 anos', ate_10_anos: 'Até 10 anos', mais_de_10_anos: 'Mais de 10 anos',
};
const MAP_PREOP_QUEIXAS = {
  dor_axial: 'Dor axial', radiculopatia: 'Radiculopatia', perda_forca: 'Perda de força',
  perda_sensibilidade: 'Perda de sensibilidade', claudicacao: 'Claudicação',
  perda_controle_esfincteriano: 'Perda de controle esfincteriano',
};
const MAP_PREOP_PELE = {
  integra: 'Íntegra', cicatriz_mielomeningocele: 'Cicatriz - Mielomeningocele',
  cicatriz_abordagem_previa: 'Cicatriz - Abordagem prévia', exposicao: 'Exposição', fistula: 'Fístula',
};
const MAP_PREOP_NEURO = {
  preservado: 'Preservado', acometimento_radicular_mmss: 'Acomet. Radicular MMSS',
  acometimento_medular_mmss: 'Acomet. Medular MMSS', acometimento_radicular_mmii: 'Acomet. Radicular MMII',
  acometimento_medular_mmii: 'Acomet. Medular MMII',
};
const MAP_PREOP_URINA = {
  espontaneo: 'Espontâneo', fralda: 'Fralda', sonda_alivio: 'Sonda de Alívio', cateter_demora: 'Cateter de Demora',
};
const MAP_PREOP_CIR = {
  nao: 'Não', discectomia_mesmo_sitio: 'Discectomia mesmo sítio',
  artrodese_mesmo_sitio: 'Artrodese mesmo sítio', discectomia_outro_sitio: 'Discectomia outro sítio',
  artrodese_outro_sitio: 'Artrodese outro sítio',
};

function tradArrPreOp(map, arr) {
  if (!arr || !arr.length) return '';
  return arr.map(v => map[v] || v).join(', ');
}

const RADIO_CAMPOS = [
  ["cobb_proximal","Cobb proximal"],["cobb_principal","Cobb principal"],
  ["cobb_lombar","Cobb lombar"],["sva","SVA"],["csva","cSVA"],
  ["lordose_cervical","Lordose cervical"],["lordose_lombar","Lordose lombar"],
  ["cifose_t5_t12","Cifose T5-T12"],["deformidade_segmentar","Deformidade segmentar"],
  ["slope_sacral","Slope sacral"],["pelvic_tilt","Pelvic tilt"],
  ["incidencia_pelvica","Incidência pélvica"],["obliquidade_pelvica","Obliquidade pélvica"],
];

function fmtData(d) {
  if (!d) return '';
  try { return format(new Date(d.includes('T') ? d : d + 'T12:00:00'), 'dd/MM/yyyy'); }
  catch { return d; }
}

function calcIdade(dob) {
  if (!dob) return '';
  try { return differenceInYears(new Date(), new Date(dob + 'T12:00:00')); }
  catch { return ''; }
}

export default function ExportacaoPage() {
  const [pesquisas, setPesquisas] = useState([]);
  const [pesquisaSel, setPesquisaSel] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    supabase.from('pesquisas').select('id, titulo').order('titulo')
      .then(({ data }) => setPesquisas(data || []));
  }, []);

  async function exportar() {
    if (!pesquisaSel) return;
    setLoading(true);
    setStatus('Buscando dados...');

    const pesquisa = pesquisas.find(p => p.id === pesquisaSel);
    const momentos = (await supabase.from('pesquisa_momentos').select('*').eq('pesquisa_id', pesquisaSel).order('ordem')).data || [];
    const paramRes = (await supabase.from('pesquisa_parametros_radio').select('*').eq('pesquisa_id', pesquisaSel).maybeSingle()).data || {};
    const scoresVinc = (await supabase.from('pesquisa_scores').select('score_id, scores(id, nome)').eq('pesquisa_id', pesquisaSel)).data || [];
    const scores = scoresVinc.map(s => s.scores).filter(Boolean);

    const ppList = (await supabase.from('pesquisa_pacientes').select(
      'id, data_inclusao, paciente_id(id, iniciais, prontuario, data_nascimento, sexo)'
    ).eq('pesquisa_id', pesquisaSel)).data || [];

    if (!ppList.length) { setStatus('Nenhum paciente nesta pesquisa.'); setLoading(false); return; }

    const ppIds = ppList.map(p => p.id);
    const pacIds = ppList.map(p => p.paciente_id.id);

    setStatus('Carregando avaliações...');

    const [comorbRes, cirRes, avalRes, radioRes] = await Promise.all([
      supabase.from('paciente_comorbidades').select('*').in('paciente_id', pacIds),
      supabase.from('cirurgias').select('*').in('pesquisa_paciente_id', ppIds).order('data_cirurgia'),
      supabase.from('avaliacoes').select('*, scores(nome)').in('pesquisa_paciente_id', ppIds),
      supabase.from('avaliacoes_radio').select('*').in('pesquisa_paciente_id', ppIds),
    ]);

    const comorbMap = {};
    (comorbRes.data || []).forEach(c => { comorbMap[c.paciente_id] = c; });
    const cirMap = {};
    (cirRes.data || []).forEach(c => { cirMap[c.pesquisa_paciente_id] = cirMap[c.pesquisa_paciente_id] || []; cirMap[c.pesquisa_paciente_id].push(c); });
    const avaliacoes = avalRes.data || [];
    const radios = radioRes.data || [];

    const camposRadioAtivos = RADIO_CAMPOS.filter(([k]) => paramRes[k]);
    const extrasRadio = paramRes.extras || [];

    function getAval(ppId, momId, scoreId) {
      return avaliacoes.find(a => a.pesquisa_paciente_id === ppId && a.momento_id === momId && a.score_id === scoreId);
    }
    function getRadio(ppId, momId) {
      return radios.find(r => r.pesquisa_paciente_id === ppId && r.momento_id === momId);
    }

    const preOpRes = await supabase
      .from('avaliacoes_preop')
      .select('*, pacientes(prontuario, iniciais)')
      .eq('assinada', true)
      .order('criada_em', { ascending: false });
    const preOpData = preOpRes.data || [];

    setStatus('Gerando planilha...');

    // ── ABA 1: Dados por paciente (wide format) ───────────────────────────
    const cabecalho1 = [];
    const cabecalho2 = [];
    const cabecalho3 = [];

    // Demográficos
    const demCols = ['Prontuário','Iniciais','Sexo','Idade','Data nasc.','Inclusão','Data cirurgia'];
    demCols.forEach(c => { cabecalho1.push('DADOS DEMOGRÁFICOS'); cabecalho2.push(''); cabecalho3.push(c); });

    // Comorbidades
    const combCols = ['HAS','Diabetes','Tabagismo','Obesidade','Osteoporose','D. Renal','Cardiopatia','Outras'];
    combCols.forEach(c => { cabecalho1.push('COMORBIDADES'); cabecalho2.push(''); cabecalho3.push(c); });

    // Cirurgia
    const cirCols = ['Tipo','Abordagem','Nível proximal','Nível distal','Nº níveis','Sangramento (ml)','Tempo (min)','Implante','Intercorrências'];
    cirCols.forEach(c => { cabecalho1.push('CIRURGIA'); cabecalho2.push(''); cabecalho3.push(c); });

    // Momentos
    momentos.forEach(m => {
      scores.forEach(s => {
        cabecalho1.push(m.label.toUpperCase()); cabecalho1.push('');
        cabecalho2.push(s.nome); cabecalho2.push('');
        cabecalho3.push('Pontuação'); cabecalho3.push('Interpretação');
      });
      camposRadioAtivos.forEach(([,l]) => {
        cabecalho1.push(m.label.toUpperCase());
        cabecalho2.push('Radiografia');
        cabecalho3.push(`${l} (°)`);
      });
      extrasRadio.forEach(e => {
        cabecalho1.push(m.label.toUpperCase());
        cabecalho2.push('Radiografia');
        cabecalho3.push(`${e} (°)`);
      });
    });

    const rows1 = ppList.map(pp => {
      const pac = pp.paciente_id;
      const cir = (cirMap[pp.id] || [])[0];
      const comb = comorbMap[pac.id] || {};
      const row = [];

      // Demográficos
      row.push(pac.prontuario, pac.iniciais,
        pac.sexo === 'M' ? 'Masc.' : pac.sexo === 'F' ? 'Fem.' : 'Outro',
        calcIdade(pac.data_nascimento), fmtData(pac.data_nascimento),
        fmtData(pp.data_inclusao), cir ? fmtData(cir.data_cirurgia) : '');

      // Comorbidades
      ['has','diabetes','tabagismo','obesidade','osteoporose','doenca_renal','cardiopatia']
        .forEach(k => row.push(comb[k] ? 'Sim' : 'Não'));
      row.push(comb.outras || '');

      // Cirurgia
      row.push(
        cir?.tipo_cirurgia || '', cir?.abordagem || '',
        cir?.nivel_proximal || '', cir?.nivel_distal || '',
        cir?.numero_niveis || '', cir?.sangramento_ml || '',
        cir?.tempo_cirurgico_min || '', cir?.implante || '',
        cir?.intercorrencias || ''
      );

      // Momentos
      momentos.forEach(m => {
        scores.forEach(s => {
          const aval = getAval(pp.id, m.id, s.id);
          row.push(aval?.pontuacao_total ?? '', aval?.interpretacao || '');
        });
        const radio = getRadio(pp.id, m.id);
        camposRadioAtivos.forEach(([k]) => row.push(radio?.[k] ?? ''));
        extrasRadio.forEach(e => row.push(radio?.extras?.[e] ?? ''));
      });

      return row;
    });

    // ── ABA 2: Avaliações brutas ──────────────────────────────────────────
    const cabAvl = ['Prontuário','Iniciais','Momento','Score','Pontuação','Interpretação','Data','Status'];
    const ppMap = Object.fromEntries(ppList.map(p => [p.id, p]));
    const rowsAvl = avaliacoes.map(a => {
      const pp = ppMap[a.pesquisa_paciente_id] || {};
      const pac = pp.paciente_id || {};
      const mom = momentos.find(m => m.id === a.momento_id) || {};
      return [pac.prontuario||'', pac.iniciais||'', mom.label||'',
        a.scores?.nome||'', a.pontuacao_total??'', a.interpretacao||'',
        fmtData(a.criado_em), a.status||''];
    });

    // ── ABA 3: Radiografia ────────────────────────────────────────────────
    const cabRad = ['Prontuário','Iniciais','Momento','Data',
      ...camposRadioAtivos.map(([,l]) => `${l} (°)`),
      ...extrasRadio.map(e => `${e} (°)`)];
    const rowsRad = radios.map(r => {
      const pp = ppMap[r.pesquisa_paciente_id] || {};
      const pac = pp.paciente_id || {};
      const mom = momentos.find(m => m.id === r.momento_id) || {};
      return [pac.prontuario||'', pac.iniciais||'', mom.label||'', fmtData(r.data_avaliacao),
        ...camposRadioAtivos.map(([k]) => r[k] ?? ''),
        ...extrasRadio.map(e => r.extras?.[e] ?? '')];
    });

    // ── Montar workbook ───────────────────────────────────────────────────
    const wb = XLSX.utils.book_new();

    // Aba 1
    const ws1Data = [cabecalho1, cabecalho2, cabecalho3, ...rows1];
    const ws1 = XLSX.utils.aoa_to_sheet(ws1Data);
    // Mesclar células do cabeçalho 1 (grupos)
    ws1['!merges'] = [];
    let mergeStart = 0;
    for (let c = 1; c <= cabecalho1.length; c++) {
      if (c === cabecalho1.length || (cabecalho1[c] !== cabecalho1[c-1] || c === cabecalho1.length)) {
        if (c - 1 > mergeStart) {
          ws1['!merges'].push({ s: { r: 0, c: mergeStart }, e: { r: 0, c: c - 1 } });
        }
        mergeStart = c;
      }
    }
    ws1['!cols'] = cabecalho3.map((_, i) => ({ wch: i < 7 ? 14 : 20 }));
    XLSX.utils.book_append_sheet(wb, ws1, 'Dados por Paciente');

    // Aba 2
    const ws2 = XLSX.utils.aoa_to_sheet([cabAvl, ...rowsAvl]);
    ws2['!cols'] = cabAvl.map(() => ({ wch: 22 }));
    XLSX.utils.book_append_sheet(wb, ws2, 'Avaliações');

    // Aba 3
    const ws3 = XLSX.utils.aoa_to_sheet([cabRad, ...rowsRad]);
    ws3['!cols'] = cabRad.map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(wb, ws3, 'Radiografia');

    // Aba 4: Anamnese Pré-Op
    const cabPreOp = ['Prontuário','Iniciais','Data Avaliação','Tempo de Sintomas','Queixas','Pele','Neurológico','Nível Medular','Raízes Afetadas','Urina','Cirurgia Prévia','Ano Cirurgia Prévia','Infecção Prévia','Diagnósticos','Diagnóstico Outro'];
    const rowsPreOp = preOpData.map(r => [
      r.pacientes?.prontuario || '',
      r.pacientes?.iniciais || '',
      fmtData(r.criada_em),
      MAP_PREOP_TEMPO[r.tempo_sintomas] || r.tempo_sintomas || '',
      tradArrPreOp(MAP_PREOP_QUEIXAS, r.queixas),
      MAP_PREOP_PELE[r.pele] || r.pele || '',
      tradArrPreOp(MAP_PREOP_NEURO, r.neurologico),
      r.nivel_medular || '',
      r.raizes_afetadas || '',
      MAP_PREOP_URINA[r.urina] || r.urina || '',
      tradArrPreOp(MAP_PREOP_CIR, r.cirurgia_previa),
      r.ano_cirurgia_previa != null ? r.ano_cirurgia_previa : '',
      r.infeccao_previa === true ? 'Sim' : r.infeccao_previa === false ? 'Não' : '',
      (r.diagnosticos || []).join('; '),
      r.diagnostico_outro || '',
    ]);
    const ws4 = XLSX.utils.aoa_to_sheet([cabPreOp, ...rowsPreOp]);
    ws4['!cols'] = cabPreOp.map(() => ({ wch: 22 }));
    XLSX.utils.book_append_sheet(wb, ws4, 'Anamnese_PreOp');

    // Download
    const nomePesquisa = pesquisa?.titulo?.replace(/[^a-zA-Z0-9]/g, '_') || 'Pesquisa';
    const nomeArquivo = `${nomePesquisa}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    XLSX.writeFile(wb, nomeArquivo);

    setStatus('');
    setLoading(false);
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Exportação de dados</h1>
          <p>Gere planilhas Excel com todos os dados de uma pesquisa</p>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 600 }}>
        <div className="card-body">
          <div className="form-group">
            <label className="form-label">Selecione a pesquisa</label>
            <select className="form-select" value={pesquisaSel} onChange={e => setPesquisaSel(e.target.value)}>
              <option value="">Selecione...</option>
              {pesquisas.map(p => <option key={p.id} value={p.id}>{p.titulo}</option>)}
            </select>
          </div>

          {pesquisaSel && (
            <div className="alert alert-info" style={{ marginBottom: 16 }}>
              O arquivo Excel terá <strong>4 abas</strong>:
              <ul style={{ marginTop: 8, paddingLeft: 20, fontSize: 13 }}>
                <li><strong>Dados por Paciente</strong> — uma linha por paciente, com todos os scores e radiografias por momento (wide format)</li>
                <li><strong>Avaliações</strong> — histórico completo de todas as avaliações</li>
                <li><strong>Radiografia</strong> — todos os parâmetros radiográficos por momento</li>
                <li><strong>Anamnese_PreOp</strong> — avaliações pré-operatórias assinadas (todos os pacientes)</li>
              </ul>
            </div>
          )}

          <button
            className="btn btn-primary"
            onClick={exportar}
            disabled={!pesquisaSel || loading}
            style={{ width: '100%', justifyContent: 'center', padding: 12 }}
          >
            {loading ? (
              <><span className="spinner" /> {status}</>
            ) : (
              '⬇ Exportar Excel'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
